import React, { useState, useRef, useCallback, memo, useMemo } from 'react';
import { RotatingLines } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import Icon from '../../icons/Icon';
import { Input } from '../common-inputs';
import { ICONS } from '../../icons';
import useBodyClicker from '../../hooks/useBodyClicker';
import Emoji from '../emoji/Emoji';
import HighlightedText from '../highlighted-text/HighlightedText';

import './CommonDropdown.scss';

const CommonDropdown = ({
  options,
  isSearchable = false,
  isFilterable = false,
  isLoading = false,
  onSelect,
  onSearch,
  disabled = false,
  value = '',
  placeholder,
  maxWidth,
  LeftIcon = null,
  withIcons = false,
  isEmojiIcons,
  onBlur,
  onFocus,
  name,
  isError,
  isReadOnly,
  isTop,
  isBorder = false,
}) => {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useBodyClicker(() => handleClickOutside());
  const isDirty = useRef(false);
  const style = { maxWidth };

  const formattedOptions = useMemo(() => {
    const filteredOptions = isFilterable
      ? options.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
      : options;

    return filteredOptions;
  }, [options, searchQuery, isFilterable]);

  const selectedOption = useMemo(() => options.find((op) => op.value === value), [value, options]);

  const handleSelectOption = useCallback(
    (value) => {
      setDropdownOpen(false);
      onSelect(value);
      setSearchQuery('');
      isDirty.current = false;
    },
    [onSelect],
  );

  const handleChangeSearch = useCallback(
    (newSearch) => {
      if (isFilterable || isSearchable) {
        isDirty.current = true;
        setSearchQuery(newSearch);
      }

      if (isSearchable && onSearch) {
        onSearch(newSearch);
      }
    },
    [onSearch, isSearchable, isFilterable],
  );

  const handleClickOutside = useCallback(() => {
    setDropdownOpen(false);
    setSearchQuery('');
    isDirty.current = false;
  }, []);

  const openDropdown = useCallback(() => {
    !disabled && !isReadOnly ? setDropdownOpen(true) : null;
  }, [disabled, isReadOnly]);

  return (
    <div className={clsx('common-dropdown')} ref={dropdownRef} style={style}>
      <Input
        onClick={openDropdown}
        extraInputClass={clsx(!disabled && !isSearchable && !isFilterable && !isReadOnly && 'common-dropdown--pointer')}
        value={isDirty.current ? searchQuery : selectedOption?.label || ''}
        placeholder={placeholder}
        isReadOnly={(!isSearchable && !isFilterable) || disabled || isReadOnly}
        debounce={isSearchable || isFilterable}
        onChange={handleChangeSearch}
        size='sm'
        disabled={disabled}
        LeftIcon={!!LeftIcon && LeftIcon}
        onBlur={onBlur}
        onFocus={onFocus}
        name={name}
        isError={isError}
        RightIcon={
          isLoading ? (
            <RotatingLines strokeColor='#fdbf47' height={10} width={20} />
          ) : (
            <Icon
              icon={ICONS.CaretUp}
              disableCommonStyles
              className={clsx(
                'common-dropdown_icon',
                dropdownOpen ? 'common-dropdown_icon--up' : 'common-dropdown_icon--down',
              )}
            />
          )
        }
      />

      {dropdownOpen && (
        <div
          className={clsx(
            'common-dropdown_menu',
            isTop && 'common-dropdown_menu--top',
            isBorder && 'common-dropdown_menu--border',
          )}>
          {formattedOptions.length === 0 ? (
            <div className='common-dropdown_no-options common-dropdown_item'>No options found</div>
          ) : (
            <ul className='common-dropdown_options'>
              {formattedOptions.map(({ value, label, disabled, icon, iconClassName, isStrokeIcon }) => (
                <li
                  className={clsx(
                    'common-dropdown_item',
                    withIcons && 'common-dropdown_item--with-icon',
                    disabled && 'common-dropdown_item--disabled',
                    !label && 'common-dropdown_item--empty',
                  )}
                  key={value}
                  role='presentation'
                  onClick={() => (!disabled ? handleSelectOption(value) : null)}>
                  {withIcons ? (
                    icon ? (
                      isEmojiIcons ? (
                        <Emoji symbol={icon} label={value} />
                      ) : (
                        <Icon
                          icon={icon}
                          disableCommonColors
                          className={clsx(
                            'common-dropdown_item_icon',
                            iconClassName,
                            isStrokeIcon ? 'common-dropdown_item_icon--stroke' : 'common-dropdown_item_icon--fill',
                          )}
                        />
                      )
                    ) : (
                      <div className='common-dropdown_item_empty-icon'></div>
                    )
                  ) : null}
                  {label ? <HighlightedText value={label} searchTerm={searchQuery} /> : t('empty')}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(CommonDropdown);
